// The News screen: a catalog of RSS/Atom sources the household can turn on,
// a rotating hero, and sports (F1 from Jolpica, teams from ESPN). A fixed
// catalog (rather than any URL) keeps the Worker from fetching arbitrary
// addresses. Publishers offer these feeds for personal reading with
// attribution; re-check terms before any commercial release.

import { decodeEntities } from "./feeds";

export type NewsCategory = "local" | "mountains" | "national" | "world" | "business" | "tech" | "sports" | "motorsport" | "outdoors" | "family";

export const NEWS_CATEGORIES: { key: NewsCategory; label: string }[] = [
  { key: "local", label: "Colorado" },
  { key: "mountains", label: "Mountain towns" },
  { key: "national", label: "National" },
  { key: "world", label: "World" },
  { key: "business", label: "Business" },
  { key: "tech", label: "Tech & science" },
  { key: "sports", label: "Sports" },
  { key: "motorsport", label: "Motorsport" },
  { key: "outdoors", label: "Outdoors" },
  { key: "family", label: "Good news & kids" },
];

export interface NewsSource {
  id: string;
  name: string;
  category: NewsCategory;
  url: string;
  /** Badge color on headlines. */
  color: string;
  /**
   * Read through the wall computer at home (kiosk/files/kinmantle-photos, /v1/feed): the
   * publisher's CDN answers Cloudflare's addresses with its home page instead of the feed.
   */
  viaHome?: boolean;
}

const S = (id: string, name: string, category: NewsCategory, url: string, color: string, viaHome?: boolean): NewsSource => ({ id, name, category, url, color, ...(viaHome ? { viaHome } : {}) });

export const NEWS_SOURCES: NewsSource[] = [
  S("cpr", "CPR News", "local", "https://www.cpr.org/feed/", "#c8102e"),
  S("9news", "9NEWS", "local", "https://www.9news.com/feeds/syndication/rss/news", "#0a58ca", true),
  S("9news-local", "9NEWS Local", "local", "https://www.9news.com/feeds/syndication/rss/news/local", "#0a58ca", true),
  S("cbs-colorado", "CBS Colorado", "local", "https://www.cbsnews.com/colorado/latest/rss/main", "#1d3c78"),
  S("kdvr", "FOX31 Denver", "local", "https://kdvr.com/feed/", "#003a70"),
  S("denver7", "Denver7", "local", "https://www.denver7.com/news/local-news.rss", "#e31b23"),
  S("colorado-sun", "The Colorado Sun", "local", "https://coloradosun.com/feed/", "#f2a900"),
  S("denverite", "Denverite", "local", "https://denverite.com/feed/", "#ff5a36"),
  S("westword", "Westword", "local", "https://www.westword.com/denver/Rss.xml", "#d0021b"),

  // Mountain-town papers: also where the Resorts screen finds each ski area's news (shared/mountains.ts papers).
  S("summit-daily", "Summit Daily", "mountains", "https://www.summitdaily.com/feed/", "#0d4c8b"),
  S("sky-hi-news", "Sky-Hi News", "mountains", "https://www.skyhinews.com/feed/", "#1f6f8b"),
  S("vail-daily", "Vail Daily", "mountains", "https://www.vaildaily.com/feed/", "#003b71"),
  S("steamboat-pilot", "Steamboat Pilot", "mountains", "https://www.steamboatpilot.com/feed/", "#1b5e20"),
  S("aspen-times", "The Aspen Times", "mountains", "https://www.aspentimes.com/feed/", "#7a1f1f"),
  S("post-independent", "Post Independent", "mountains", "https://www.postindependent.com/feed/", "#2b4c7e"),
  S("durango-herald", "The Durango Herald", "mountains", "https://www.durangoherald.com/feed/", "#004b87"),
  S("crested-butte-news", "Crested Butte News", "mountains", "https://crestedbuttenews.com/feed/", "#2e5d3a"),
  S("telluride-planet", "Telluride Daily Planet", "mountains", "https://www.telluridenews.com/search/?f=rss", "#5b3a8c"),

  S("npr", "NPR News", "national", "https://feeds.npr.org/1001/rss.xml", "#237bbd"),
  S("npr-politics", "NPR Politics", "national", "https://feeds.npr.org/1014/rss.xml", "#237bbd"),
  S("msnow", "MS NOW", "national", "https://www.ms.now/feed", "#6460aa"),
  S("pbs", "PBS NewsHour", "national", "https://www.pbs.org/newshour/feeds/rss/headlines", "#2638c4"),
  S("abc", "ABC News", "national", "https://abcnews.go.com/abcnews/topstories", "#000000"),
  S("cbs", "CBS News", "national", "https://www.cbsnews.com/latest/rss/main", "#1d3c78"),
  S("nbc", "NBC News", "national", "https://feeds.nbcnews.com/nbcnews/public/news", "#6460aa"),
  S("cnn", "CNN", "national", "http://rss.cnn.com/rss/cnn_topstories.rss", "#cc0000"),
  S("fox", "Fox News", "national", "https://moxie.foxnews.com/google-publisher/latest.xml", "#003366"),
  S("nyt", "The New York Times", "national", "https://rss.nytimes.com/services/xml/rss/nyt/HomePage.xml", "#121212"),
  S("guardian-us", "The Guardian US", "national", "https://www.theguardian.com/us-news/rss", "#052962"),
  S("axios", "Axios", "national", "https://api.axios.com/feed/", "#2a3a8f"),
  S("politico", "Politico", "national", "https://rss.politico.com/politics-news.xml", "#b5121b"),
  S("the-hill", "The Hill", "national", "https://thehill.com/feed/", "#1a1a1a"),

  S("bbc", "BBC World", "world", "https://feeds.bbci.co.uk/news/world/rss.xml", "#bb1919"),
  S("bbc-us", "BBC US & Canada", "world", "https://feeds.bbci.co.uk/news/world/us_and_canada/rss.xml", "#bb1919"),
  S("npr-world", "NPR World", "world", "https://feeds.npr.org/1004/rss.xml", "#237bbd"),
  S("al-jazeera", "Al Jazeera", "world", "https://www.aljazeera.com/xml/rss/all.xml", "#fa9000"),

  S("npr-business", "NPR Business", "business", "https://feeds.npr.org/1006/rss.xml", "#237bbd"),
  S("marketwatch", "MarketWatch", "business", "https://feeds.content.dowjones.io/public/rss/mw_topstories", "#00ac4e"),
  S("cnbc", "CNBC", "business", "https://www.cnbc.com/id/100003114/device/rss/rss.html", "#005594"),
  S("wsj", "WSJ World", "business", "https://feeds.a.dj.com/rss/RSSWorldNews.xml", "#0274b6"),

  S("verge", "The Verge", "tech", "https://www.theverge.com/rss/index.xml", "#5200ff"),
  S("ars", "Ars Technica", "tech", "https://feeds.arstechnica.com/arstechnica/index", "#ff4e00"),
  S("wired", "WIRED", "tech", "https://www.wired.com/feed/rss", "#000000"),
  S("engadget", "Engadget", "tech", "https://www.engadget.com/rss.xml", "#400090"),
  S("techcrunch", "TechCrunch", "tech", "https://techcrunch.com/feed/", "#0a9e01"),
  S("hacker-news", "Hacker News", "tech", "https://hnrss.org/frontpage", "#ff6600"),
  S("npr-tech", "NPR Technology", "tech", "https://feeds.npr.org/1019/rss.xml", "#237bbd"),
  S("npr-science", "NPR Science", "tech", "https://feeds.npr.org/1007/rss.xml", "#237bbd"),
  S("bbc-science", "BBC Science", "tech", "https://feeds.bbci.co.uk/news/science_and_environment/rss.xml", "#bb1919"),
  S("npr-health", "NPR Health", "tech", "https://feeds.npr.org/1128/rss.xml", "#237bbd"),
  S("nasa", "NASA", "tech", "https://www.nasa.gov/news-release/feed/", "#0b3d91"),
  S("space", "Space.com", "tech", "https://www.space.com/feeds/all", "#1b2a4a"),
  S("science-daily", "ScienceDaily", "tech", "https://www.sciencedaily.com/rss/top/science.xml", "#1c5d99"),
  S("smithsonian", "Smithsonian", "tech", "https://www.smithsonianmag.com/rss/latest_articles/", "#3c3c3c"),

  S("espn", "ESPN", "sports", "https://www.espn.com/espn/rss/news", "#d00000"),
  S("espn-nfl", "ESPN NFL", "sports", "https://www.espn.com/espn/rss/nfl/news", "#d00000"),
  S("espn-nba", "ESPN NBA", "sports", "https://www.espn.com/espn/rss/nba/news", "#d00000"),
  S("espn-nhl", "ESPN NHL", "sports", "https://www.espn.com/espn/rss/nhl/news", "#d00000"),
  S("espn-mlb", "ESPN MLB", "sports", "https://www.espn.com/espn/rss/mlb/news", "#d00000"),
  S("espn-cfb", "ESPN College Football", "sports", "https://www.espn.com/espn/rss/ncf/news", "#d00000"),
  S("espn-soccer", "ESPN Soccer", "sports", "https://www.espn.com/espn/rss/soccer/news", "#d00000"),
  S("cbs-sports", "CBS Sports", "sports", "https://www.cbssports.com/rss/headlines/", "#0c3c7c"),
  S("cbs-sports-nfl", "CBS Sports NFL", "sports", "https://www.cbssports.com/rss/headlines/nfl/", "#0c3c7c"),
  S("yahoo-sports", "Yahoo Sports", "sports", "https://sports.yahoo.com/rss/", "#6001d2"),
  S("broncos", "Denver Broncos", "sports", "https://www.denverbroncos.com/rss/news", "#fb4f14"),
  S("mile-high-report", "Mile High Report", "sports", "https://www.milehighreport.com/rss/current.xml", "#fb4f14"),
  S("mile-high-hockey", "Mile High Hockey", "sports", "https://www.milehighhockey.com/rss/current.xml", "#6f263d"),
  S("rockies", "Colorado Rockies", "sports", "https://www.mlb.com/rockies/feeds/news/rss.xml", "#33006f"),

  S("f1", "Formula 1", "motorsport", "https://www.formula1.com/en/latest/all.xml", "#e10600"),
  S("motorsport-f1", "Motorsport.com F1", "motorsport", "https://www.motorsport.com/rss/f1/news/", "#e10600"),
  S("autosport-f1", "Autosport F1", "motorsport", "https://www.autosport.com/rss/f1/news/", "#ef3340"),
  S("espn-racing", "ESPN Racing", "motorsport", "https://www.espn.com/espn/rss/rpm/news", "#d00000"),
  S("racefans", "RaceFans", "motorsport", "https://www.racefans.net/feed/", "#c8102e"),

  S("ski", "SKI Magazine", "outdoors", "https://www.skimag.com/feed/", "#0072ce"),
  S("powder", "POWDER", "outdoors", "https://www.powder.com/feed/", "#111111"),
  S("outside", "Outside", "outdoors", "https://www.outsideonline.com/feed/", "#ffd400"),
  S("snowbrains", "SnowBrains", "outdoors", "https://www.snowbrains.com/feed/", "#1e88e5"),

  S("good-news", "Good News Network", "family", "https://www.goodnewsnetwork.org/feed/", "#2e9e44"),
  S("upworthy", "Upworthy", "family", "https://www.upworthy.com/feeds/feed.rss", "#ff6f61"),
  S("time-for-kids", "TIME for Kids", "family", "https://www.timeforkids.com/feed/", "#e90000"),
];

export function sourceById(id: string): NewsSource | undefined {
  return NEWS_SOURCES.find((s) => s.id === id);
}

// ---------- Sports ----------

export type League = "nfl" | "nba" | "nhl" | "mlb" | "mls" | "wnba" | "college-football" | "mens-college-basketball";

export const LEAGUES: Record<League, { name: string; path: string }> = {
  nfl: { name: "NFL", path: "football/nfl" },
  nba: { name: "NBA", path: "basketball/nba" },
  nhl: { name: "NHL", path: "hockey/nhl" },
  mlb: { name: "MLB", path: "baseball/mlb" },
  mls: { name: "MLS", path: "soccer/usa.1" },
  wnba: { name: "WNBA", path: "basketball/wnba" },
  "college-football": { name: "College football", path: "football/college-football" },
  "mens-college-basketball": { name: "Men’s college basketball", path: "basketball/mens-college-basketball" },
};

export function isLeague(x: unknown): x is League {
  return typeof x === "string" && x in LEAGUES;
}

export interface TeamRef {
  league: League;
  /** ESPN team id or abbreviation, e.g. "den". */
  team: string;
}

export interface NewsSettings {
  /** Source ids turned on for the News screen, in the household's order. */
  sources: string[];
  /** Sources whose top stories rotate through the hero. */
  hero: string[];
  /** Seconds each hero story stays up. */
  heroSeconds: number;
  teams: TeamRef[];
  f1: boolean;
}

export const DEFAULT_NEWS: NewsSettings = { sources: ["npr", "cpr", "9news", "bbc"], hero: ["npr"], heroSeconds: 12, teams: [], f1: false };

export const MAX_NEWS_SOURCES = 16;
export const MAX_TEAMS = 4;

// ---------- Stories ----------

export interface NewsItem {
  id: string;
  title: string;
  url: string | null;
  source: string;
  publishedAt: string | null;
  image: string | null;
  summary: string | null;
}

export interface NewsSourcePayload {
  source: string;
  items: NewsItem[];
  fetchedAt: string;
  servedFromCache: boolean;
}

const attr = (tag: string, name: string) => new RegExp(`\\b${name}\\s*=\\s*["']([^"']+)["']`, "i").exec(tag)?.[1] ?? null;
const unescapeUrl = (u: string) => u.replace(/&amp;/g, "&").replace(/&#38;/g, "&").trim();

/** The biggest picture an item offers: media:content / media:thumbnail / enclosure / first <img>. */
export function itemImage(block: string): string | null {
  const candidates: { url: string; w: number }[] = [];
  for (const m of block.matchAll(/<media:(content|thumbnail)\b[^>]*>/gi)) {
    const url = attr(m[0], "url");
    const medium = attr(m[0], "medium");
    const type = attr(m[0], "type");
    if (!url) continue;
    if (medium && medium !== "image") continue;
    if (type && !type.startsWith("image")) continue;
    candidates.push({ url, w: Number(attr(m[0], "width")) || (m[1] === "thumbnail" ? 1 : 2) });
  }
  for (const m of block.matchAll(/<enclosure\b[^>]*>/gi)) {
    const url = attr(m[0], "url");
    const type = attr(m[0], "type") ?? "";
    if (url && (type.startsWith("image") || /\.(jpe?g|png|webp)(\?|$)/i.test(url))) candidates.push({ url, w: 2 });
  }
  if (!candidates.length) {
    const img = /<img\b[^>]*>/i.exec(block.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"'));
    const src = img ? attr(img[0], "src") : null;
    if (src && /^https?:/i.test(src)) candidates.push({ url: src, w: 1 });
  }
  const best = candidates.sort((a, b) => b.w - a.w)[0];
  const url = best ? unescapeUrl(best.url.replace(/<!\[CDATA\[|\]\]>/g, "")) : null;
  return url && /^https?:\/\//i.test(url) ? url.replace(/^http:/i, "https:") : null;
}

function itemLink(block: string): string | null {
  const atom = /<link\b[^>]*rel=["']alternate["'][^>]*>/i.exec(block)?.[0] ?? /<link\b[^>]*href=[^>]*>/i.exec(block)?.[0];
  if (atom) {
    const href = attr(atom, "href");
    if (href) return unescapeUrl(href);
  }
  const rss = /<link\b[^>]*>([\s\S]*?)<\/link>/i.exec(block)?.[1];
  if (rss) return unescapeUrl(rss.replace(/<!\[CDATA\[|\]\]>/g, ""));
  const guid = /<guid\b[^>]*>([\s\S]*?)<\/guid>/i.exec(block)?.[1];
  return guid && /^https?:/.test(guid.trim()) ? unescapeUrl(guid) : null;
}

/** Stories from an RSS 2.0 or Atom document, newest first as published. */
export function parseNewsItems(xml: string, source: string, max = 12): NewsItem[] {
  const blocks = xml.match(/<(item|entry)\b[\s\S]*?<\/\1>/g) ?? [];
  const out: NewsItem[] = [];
  for (const b of blocks) {
    const title = decodeEntities(/<title\b[^>]*>([\s\S]*?)<\/title>/i.exec(b)?.[1] ?? "");
    if (title.length < 6) continue;
    const date = /<(pubDate|published|updated|dc:date)\b[^>]*>([\s\S]*?)<\/\1>/i.exec(b)?.[2]?.trim();
    const t = date ? Date.parse(date) : NaN;
    const rawSummary = /<(description|summary|content)\b[^>]*>([\s\S]*?)<\/\1>/i.exec(b)?.[2] ?? "";
    const summary = decodeEntities(rawSummary.replace(/&lt;/g, "<").replace(/&gt;/g, ">"));
    const url = itemLink(b);
    out.push({
      id: `${source}:${(url ?? title).slice(-120)}`,
      title,
      url,
      source,
      publishedAt: Number.isFinite(t) ? new Date(t).toISOString() : null,
      image: itemImage(b),
      summary: summary.length > 20 ? (summary.length > 260 ? `${summary.slice(0, 257).trimEnd()}…` : summary) : null,
    });
    if (out.length >= max) break;
  }
  return out;
}

/** og:image / twitter:image from the start of an article page. */
export function pageImage(html: string): string | null {
  for (const m of html.matchAll(/<meta\b[^>]*>/gi)) {
    const prop = (attr(m[0], "property") ?? attr(m[0], "name") ?? "").toLowerCase();
    if (prop === "og:image" || prop === "og:image:url" || prop === "twitter:image") {
      const content = attr(m[0], "content");
      if (content && /^https?:\/\//i.test(content)) return unescapeUrl(content).replace(/^http:/i, "https:");
    }
  }
  return null;
}

// ---------- Reading a story in the app ----------

/** A story's readable text, pulled from its page, so it can be read on the wall instead of scanned to a phone. */
export interface Article {
  url: string;
  title: string | null;
  site: string | null;
  byline: string | null;
  publishedAt: string | null;
  image: string | null;
  /** Empty when the page has no readable story (a paywall, a video page, a blocked request). */
  paragraphs: string[];
}

/** Less readable text than this is a teaser or a paywall, not the story. */
export const MIN_ARTICLE_CHARS = 400;
const MAX_PARAGRAPHS = 120;
const MAX_ARTICLE_CHARS = 40_000;
const BOILERPLATE =
  /^(advertisement|sponsored|related:|related stories|read more|more:|see also|sign up|subscribe|follow us|share this|click here|download the|get the .{0,30}app|copyright|©|all rights reserved|watch:|listen:|image:|photo:|credit:|getty images|ap photo|support (trusted|our|local|independent) journalism)/i;
const NOISY_CLASS = /caption|credit|byline|newsletter|promo|related|share|social|advert|\bad-|signup|subscribe|disclaimer|copyright|footer|tease/i;
// Boxes inside a story that aren't the story: photo captions, a publisher's notices.
const NOISY_BOX = /<(div|section|span|p)\b[^>]*\bclass\s*=\s*["'][^"']*(caption|credit|disclaimer|newsletter|anti-fraud)[^"']*["'][^>]*>[\s\S]*?<\/\1>/gi;
const DROP_TAGS = ["script", "style", "noscript", "svg", "nav", "header", "footer", "aside", "form", "button", "iframe", "figure", "figcaption", "template", "select", "dialog"];

function metaContent(html: string, names: string[]): string | null {
  for (const m of html.matchAll(/<meta\b[^>]*>/gi)) {
    const prop = (/\b(?:property|name|itemprop)\s*=\s*["']([^"']+)["']/i.exec(m[0])?.[1] ?? "").toLowerCase();
    if (!names.includes(prop)) continue;
    const c = /\bcontent\s*=\s*(?:"([^"]*)"|'([^']*)')/i.exec(m[0]);
    const v = decodeEntities(c?.[1] ?? c?.[2] ?? "");
    if (v) return v;
  }
  return null;
}

interface LdArticle {
  body: string | null;
  headline: string | null;
  author: string | null;
  date: string | null;
}

/** The page's own structured copy of the story (JSON-LD NewsArticle and kin), when it has one. */
function ldArticle(html: string): LdArticle | null {
  const name = (a: unknown): string | null => {
    if (typeof a === "string") return a;
    if (Array.isArray(a)) return a.map(name).filter(Boolean).join(", ") || null;
    if (a && typeof a === "object" && typeof (a as { name?: unknown }).name === "string") return (a as { name: string }).name;
    return null;
  };
  const visit = (node: unknown): LdArticle | null => {
    if (Array.isArray(node)) {
      for (const n of node) {
        const hit = visit(n);
        if (hit) return hit;
      }
      return null;
    }
    if (!node || typeof node !== "object") return null;
    const o = node as Record<string, unknown>;
    const type = ([] as unknown[]).concat(o["@type"] ?? []).join(" ");
    if (/Article|BlogPosting|Report/i.test(type) && (typeof o.articleBody === "string" || typeof o.headline === "string")) {
      return {
        body: typeof o.articleBody === "string" ? o.articleBody : null,
        headline: typeof o.headline === "string" ? decodeEntities(o.headline) : null,
        // Some sites put HTML entities in their structured data ("Pilot &amp; Today").
        author: ((n) => (n ? decodeEntities(n) : null))(name(o.author)),
        date: typeof o.datePublished === "string" ? o.datePublished : null,
      };
    }
    return visit(o["@graph"]);
  };
  for (const m of html.matchAll(/<script\b[^>]*type\s*=\s*["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi)) {
    try {
      const hit = visit(JSON.parse(m[1]!.trim()));
      if (hit) return hit;
    } catch {
      /* a malformed block; try the next */
    }
  }
  return null;
}

/** The story body as paragraphs: its own line breaks, else a few sentences each. */
function splitBody(body: string): string[] {
  const lines = body
    .split(/\n+/)
    .map((l) => decodeEntities(l))
    .filter((l) => l.length > 1);
  if (lines.length > 1) return lines;
  // A sentence ends where the next starts with a capital ("6 a.m. and" isn't an end).
  const sentences = body.trim().split(/(?<=[.!?]["”’)]?)\s+(?=["“‘(]?[A-Z0-9])/);
  const out: string[] = [];
  for (let i = 0; i < sentences.length; i += 3) out.push(decodeEntities(sentences.slice(i, i + 3).join(" ")));
  return out;
}

function keep(text: string): boolean {
  return text.length >= 40 && /[a-z]/.test(text) && !BOILERPLATE.test(text);
}

const totalChars = (ps: string[]) => ps.reduce((n, p) => n + p.length, 0);

/** The <p> text of the page's main story: its <article> with the most text, else the whole page. */
function htmlParagraphs(html: string): string[] {
  let doc = html.replace(/<!--[\s\S]*?-->/g, " ");
  for (const tag of DROP_TAGS) doc = doc.replace(new RegExp(`<${tag}\\b[\\s\\S]*?<\\/${tag}>`, "gi"), " ");
  doc = doc.replace(NOISY_BOX, " ");
  const paras = (region: string) => {
    const out: string[] = [];
    for (const m of region.matchAll(/<p\b([^>]*)>([\s\S]*?)<\/p>/gi)) {
      if (NOISY_CLASS.test(m[1] ?? "")) continue;
      const text = decodeEntities(m[2] ?? "");
      if (keep(text)) out.push(text);
    }
    return out;
  };
  const candidates = [...doc.matchAll(/<article\b[\s\S]*?<\/article>/gi)].map((m) => paras(m[0]));
  const best = candidates.sort((a, b) => totalChars(b) - totalChars(a))[0];
  return best && totalChars(best) >= MIN_ARTICLE_CHARS ? best : paras(doc);
}

/** The readable story from an article page (shown in the app by widgets/news/ArticleSheet). */
export function readArticle(html: string, url: string): Article {
  const ld = ldArticle(html);
  const found = ld?.body && ld.body.length >= MIN_ARTICLE_CHARS ? splitBody(ld.body) : htmlParagraphs(html);
  // A long story stops at a paragraph, not mid-sentence.
  const kept: string[] = [];
  let chars = 0;
  for (const p of new Set(found)) {
    if (kept.length >= MAX_PARAGRAPHS || chars + p.length > MAX_ARTICLE_CHARS) break;
    kept.push(p);
    chars += p.length;
  }
  const docTitle = /<title\b[^>]*>([\s\S]*?)<\/title>/i.exec(html)?.[1];
  const published = ld?.date ?? metaContent(html, ["article:published_time", "datepublished", "pubdate", "date"]);
  const t = published ? Date.parse(published) : NaN;
  return {
    url,
    title: metaContent(html, ["og:title", "twitter:title"]) ?? ld?.headline ?? (docTitle ? decodeEntities(docTitle) : null),
    site: metaContent(html, ["og:site_name", "application-name"]),
    byline: ld?.author ?? metaContent(html, ["author", "article:author", "byl", "parsely-author"]),
    publishedAt: Number.isFinite(t) ? new Date(t).toISOString() : null,
    image: pageImage(html),
    paragraphs: chars >= MIN_ARTICLE_CHARS ? kept : [],
  };
}

// ---------- Sports payloads ----------

export interface F1Payload {
  season: string;
  round: string | null;
  next: {
    name: string;
    round: string;
    circuit: string;
    locality: string;
    country: string;
    /** Race start (UTC ISO). */
    start: string;
    qualifying: string | null;
    sprint: string | null;
    image: string | null;
  } | null;
  last: { name: string; podium: { code: string; name: string; team: string }[] } | null;
  /** team, teamId and wins come from Jolpica when it answers ("" / null otherwise). */
  drivers: { position: number; code: string; name: string; team: string; teamId: string; color: string | null; flag: string | null; points: number; wins: number | null }[];
  constructors: { position: number; name: string; id: string; color: string | null; points: number }[];
  fetchedAt: string;
}

export interface TeamGame {
  id: string;
  date: string;
  /** "DEN @ ARI" */
  shortName: string;
  home: boolean;
  opponent: { name: string; abbr: string; logo: string | null };
  venue: string | null;
  broadcast: string | null;
  state: "pre" | "in" | "post";
  /** Final or live score from the team's side, e.g. "W 24–17". */
  result: string | null;
}

export interface TeamPayload {
  league: League;
  team: { id: string; name: string; abbr: string; logo: string | null; color: string | null; record: string | null; standing: string | null };
  next: TeamGame | null;
  last: TeamGame | null;
  /** The team's division or conference table. */
  standings: { group: string; rows: { abbr: string; name: string; logo: string | null; record: string; gb: string | null; isTeam: boolean }[] } | null;
  news: NewsItem[];
  fetchedAt: string;
}
